var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import React from 'jsx-dom';
import { CSSClassUtils } from '@exadel/esl/modules/esl-utils/dom';
import { skipOneRender } from '@exadel/esl/modules/esl-utils/async';
import { ESLMediaQuery } from '@exadel/esl/modules/esl-media-query/core';
import { attr, boolAttr, listen, memoize } from '@exadel/esl/modules/esl-utils/decorators';
import { UIPPlugin } from '../base/plugin';
export class UIPPluginPanel extends UIPPlugin {
    constructor() {
        super(...arguments);
        /** Start point */
        this._startPoint = NaN;
        /** Start size */
        this._startSize = NaN;
    }
    /** Plugin header icon */
    get $icon() {
        return null;
    }
    /** Plugin header additional buttons section */
    get $toolbar() {
        return null;
    }
    /** Header section block */
    get $header() {
        var _a;
        const type = this.constructor;
        const a11yLabel = this.collapsible ? 'Collapse/expand' + this.label : this.label;
        const hasToolbar = (_a = this.$toolbar) === null || _a === void 0 ? void 0 : _a.children.length;
        return (React.createElement("div", { className: type.is + '-header uip-plugin-header' + (this.label ? '' : ' no-label') + (hasToolbar ? ' has-toolbar' : '') },
            this.$icon ? React.createElement("span", { className: 'uip-plugin-header-icon', title: this.label }, this.$icon) : '',
            React.createElement("span", { class: `uip-plugin-header-title ${type.is}-title` }, this.label),
            this.collapsible ? React.createElement("button", { type: 'button', className: 'uip-plugin-header-trigger', "aria-label": a11yLabel, title: a11yLabel }) : '',
            hasToolbar ? this.$toolbar : ''));
    }
    /** Creates resize area element */
    get $resize() {
        return (React.createElement("div", { className: 'uip-plugin-resize-bar' }));
    }
    /** @returns if the plugin should be rendered vertically */
    get isVertical() {
        return ESLMediaQuery.for(this.vertical).matches;
    }
    connectedCallback() {
        super.connectedCallback();
        this.classList.add('uip-plugin-panel');
        this._onLayoutModeChange();
    }
    attributeChangedCallback(attrName, oldVal, newVal) {
        super.attributeChangedCallback(attrName, oldVal, newVal);
        if (attrName === 'vertical') {
            this.$$off(this._onLayoutModeChange);
            this.$$on(this._onLayoutModeChange);
            this._onLayoutModeChange();
        }
    }
    /** Handles collapsing trigger clicks */
    _onCollapseClick() {
        if (!this.collapsible)
            return;
        this.collapsed = !this.collapsed;
        this.resizing = false;
    }
    /** Handles vertical media query change */
    _onLayoutModeChange() {
        const { isVertical } = this;
        const type = this.constructor;
        this.$$cls('vertical', isVertical);
        CSSClassUtils.toggle(this.$root, type.is + '-vertical', isVertical);
        CSSClassUtils.add(this.$root, 'no-animate', this);
        skipOneRender(() => this.$root && CSSClassUtils.remove(this.$root, 'no-animate', this));
    }
    // Resize logic
    /** Handles resize start */
    _onPointerStart(event) {
        if (!this.resizable || this.collapsed)
            return;
        const { isVertical } = this;
        this.resizing = true;
        const prop = isVertical ? '--uip-plugin-width' : '--uip-plugin-height';
        this._startSize = parseFloat(getComputedStyle(this).getPropertyValue(prop) || '0');
        this._startPoint = isVertical ? event.x : event.y;
        if (this.$resize.setPointerCapture)
            this.$resize.setPointerCapture(event.pointerId);
        this.$$on(this._onPointerMove);
    }
    /** Handles resize end */
    _onPointerEnd(event) {
        this.resizing = false;
        if (this.$resize.releasePointerCapture)
            this.$resize.releasePointerCapture(event.pointerId);
        this.$$off(this._onPointerMove);
    }
    /** Handles resize */
    _onPointerMove(event) {
        if (!this.resizing)
            return;
        const { isVertical } = this;
        const delta = this._startPoint - (isVertical ? event.x : event.y);
        const value = Math.round(this._startSize + delta * (isVertical ? 1 : -1));
        const prop = isVertical ? '--uip-plugin-width' : '--uip-plugin-height';
        this.style.setProperty(prop, `${value}px`);
    }
}
UIPPluginPanel.observedAttributes = ['vertical', 'collapsible', ...UIPPlugin.observedAttributes];
__decorate([
    boolAttr()
], UIPPluginPanel.prototype, "collapsed", void 0);
__decorate([
    boolAttr()
], UIPPluginPanel.prototype, "collapsible", void 0);
__decorate([
    boolAttr()
], UIPPluginPanel.prototype, "resizable", void 0);
__decorate([
    boolAttr()
], UIPPluginPanel.prototype, "resizing", void 0);
__decorate([
    attr({ defaultValue: 'not all' })
], UIPPluginPanel.prototype, "vertical", void 0);
__decorate([
    memoize()
], UIPPluginPanel.prototype, "$header", null);
__decorate([
    memoize()
], UIPPluginPanel.prototype, "$resize", null);
__decorate([
    listen({
        event: 'click',
        selector: '.uip-plugin-header-trigger',
    })
], UIPPluginPanel.prototype, "_onCollapseClick", null);
__decorate([
    listen({
        event: 'change',
        target: (settings) => ESLMediaQuery.for(settings.vertical)
    })
], UIPPluginPanel.prototype, "_onLayoutModeChange", null);
__decorate([
    listen({
        event: 'pointerdown',
        selector: '.uip-plugin-resize-bar'
    })
], UIPPluginPanel.prototype, "_onPointerStart", null);
__decorate([
    listen({
        event: 'pointerup',
        selector: '.uip-plugin-resize-bar'
    })
], UIPPluginPanel.prototype, "_onPointerEnd", null);
__decorate([
    listen({
        auto: false,
        event: 'pointermove',
        selector: '.uip-plugin-resize-bar'
    })
], UIPPluginPanel.prototype, "_onPointerMove", null);
